import React, { useMemo } from "react";
import { Plus, RotateCw, Edit2, Trash2 } from "lucide-react";
import { FREQUENCIES } from "../lib/constants";

export default function RecurringTasksView({ tasks, openModal, completeTask, deleteTask }) {
  const sortedTasks = useMemo(
    () => [...tasks].sort((a, b) => {
      if (!a.nextDueDate) return 1;
      if (!b.nextDueDate) return -1;
      return new Date(a.nextDueDate).getTime() - new Date(b.nextDueDate).getTime();
    }),
    [tasks]
  );

  return (
    <div className="flex-1 bg-slate-50 p-8 overflow-y-auto custom-scrollbar" data-testid="recurring-view">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-slate-900 tracking-tight">Scadenziario Ciclico</h1>
            <p className="text-slate-500 text-sm mt-1">Clicca sull&apos;icona di rinnovo per spostare la scadenza alla prossima data utile.</p>
          </div>
          <button
            onClick={() => openModal()}
            className="bg-blue-700 hover:bg-blue-800 text-white px-5 py-2 rounded-lg font-semibold shadow-md flex items-center gap-2 transition"
            data-testid="new-recurring-btn"
          >
            <Plus size={19} /> Nuova Scadenza
          </button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <th className="p-4 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Oggetto</th>
                <th className="p-4 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Descrizione</th>
                <th className="p-4 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Prossima Scadenza</th>
                <th className="p-4 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Ciclo</th>
                <th className="p-4 text-[11px] font-bold text-slate-600 uppercase tracking-wider text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {sortedTasks.map((t) => {
                const due = new Date(t.nextDueDate); due.setHours(0, 0, 0, 0);
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                const isUrgent = diffDays <= 3;
                const isNear = diffDays > 3 && diffDays <= 7;

                const rowClass = isUrgent ? "bg-red-50" : "hover:bg-slate-50";
                const clientClass = isUrgent ? "text-red-700 font-black" : isNear ? "text-amber-700 font-bold" : "text-slate-900";
                const descClass = isUrgent ? "text-red-600" : isNear ? "text-amber-600" : "text-slate-600";
                const badgeClass = isUrgent
                  ? "bg-red-100 text-red-700 font-bold border border-red-200"
                  : isNear
                  ? "bg-amber-100 text-amber-700 font-bold border border-amber-200"
                  : "bg-slate-100 text-slate-600";

                return (
                  <tr key={t.id} className={`border-b border-slate-100 last:border-0 ${rowClass}`} data-testid={`recurring-row-${t.id}`}>
                    <td className={`p-4 font-semibold ${clientClass}`}>{t.client}</td>
                    <td className={`p-4 text-sm ${descClass}`}>{t.description}</td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs ${badgeClass}`}>
                        {t.nextDueDate ? new Date(t.nextDueDate).toLocaleDateString("it-IT") : "—"}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-500">{FREQUENCIES[t.frequency] || "Mensile"}</td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end items-center gap-1">
                        <button
                          onClick={() => completeTask(t)}
                          className={`p-2 rounded-full transition ${
                            isUrgent
                              ? "bg-red-600 text-white hover:bg-red-700 animate-pulse"
                              : "bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-600"
                          }`}
                          title="Rinnova scadenza (Completa e sposta avanti)"
                          data-testid={`renew-recurring-${t.id}`}
                        >
                          <RotateCw size={17} />
                        </button>
                        <button
                          onClick={() => openModal(t)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition"
                          data-testid={`edit-recurring-${t.id}`}
                        >
                          <Edit2 size={17} />
                        </button>
                        <button
                          onClick={() => deleteTask(t.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-full transition"
                          data-testid={`delete-recurring-${t.id}`}
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {sortedTasks.length === 0 && (
            <div className="p-10 text-center text-slate-400">Nessuna scadenza ciclica inserita.</div>
          )}
        </div>
      </div>
    </div>
  );
}
